import React, { ReactNode } from 'react'
import { Image, Platform, ScrollView, StyleSheet, View } from 'react-native'
import AutoHeightWebView from 'react-native-autoheight-webview'
import { useMutation, useQueryClient } from 'react-query'
import { format, isValid, parseISO } from 'date-fns'

import termsAndConditionsQuery from '../../api/query/termsAndConditions'
import { ITermsAndConditionsCurrent } from '../../@types/TermsAndConditions'
import Button from '../Button'
import LoadingComponent from '../LoadingComponent'
import AppText from '../Text'
import { defaultStyles } from '../../config/styles'
import { useSession } from '../../hooks/useSession'
import { QUERY_KEYS } from '../../config/queryKeys'

interface IHtmlContent {
  html: string
}

interface ITermsAndConditionsGate {
  children: ReactNode
}

const HtmlContent: React.FC<IHtmlContent> = ({ html }) => {
  if (Platform.OS === 'web') {
    return <div dangerouslySetInnerHTML={{ __html: html }} />
  }

  return (
    <AutoHeightWebView
      scrollEnabled={false}
      source={{
        html: `
          <html>
            <head>
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
            </head>
            <body style="font-family: Open Sans, Arial, sans-serif; font-size: 18px; color: #0c0c0c;">
              ${html}
            </body>
          </html>
        `,
      }}
      style={styles.htmlView}
    />
  )
}

function TermsAndConditionsGate({ children }: ITermsAndConditionsGate) {
  const { logOut } = useSession()
  const queryClient = useQueryClient()
  const currentTermsQuery = termsAndConditionsQuery.getCurrentTerms()
  const currentTerms = currentTermsQuery.data
  const hasCurrentTerms = Boolean(currentTerms?.id)
  const lastModifiedDate = currentTerms?.lastModified
    ? parseISO(currentTerms.lastModified)
    : null
  const formattedLastModified =
    lastModifiedDate && isValid(lastModifiedDate)
      ? format(lastModifiedDate, 'do MMMM yyyy')
      : 'Date unavailable'

  const acceptMutation = useMutation(termsAndConditionsQuery.acceptTerms, {
    onSuccess: (terms: ITermsAndConditionsCurrent) => {
      queryClient.setQueryData(QUERY_KEYS.termsAndConditions.current, terms)
    },
  })

  if (currentTermsQuery.isLoading) {
    return <LoadingComponent />
  }

  if (currentTermsQuery.isError) {
    return (
      <View style={styles.page}>
        <View style={styles.card}>
          <AppText style={styles.heading}>Terms & Conditions</AppText>
          <AppText style={styles.bodyText}>
            We couldn&apos;t load the current terms and conditions.
          </AppText>
          <View style={styles.buttonStack}>
            <Button title="Retry" onPress={() => currentTermsQuery.refetch()} />
            <Button
              title="Logout"
              onPress={logOut}
              backgroundColor="white"
              borderColor="secondary"
              textColor="secondary"
            />
          </View>
        </View>
      </View>
    )
  }

  if (!hasCurrentTerms || !currentTerms || currentTerms.accepted) {
    return <>{children}</>
  }

  return (
    <View style={styles.page}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.card}>
          <View style={styles.logoContainer}>
            <Image
              style={styles.logo}
              source={require('../../assets/logo-icon.png')}
            />
          </View>
          <View style={styles.htmlContainer}>
            <HtmlContent html={currentTerms.content} />
          </View>
          {acceptMutation.isError && (
            <AppText style={styles.errorText}>
              We couldn&apos;t record your acceptance. Please try again.
            </AppText>
          )}
          <View style={styles.buttonStack}>
            <Button
              title={acceptMutation.isLoading ? 'Accepting...' : 'Accept'}
              onPress={() => acceptMutation.mutate(currentTerms.id)}
              disabled={acceptMutation.isLoading}
            />
            <Button
              title="Logout"
              onPress={logOut}
              backgroundColor="white"
              borderColor="secondary"
              textColor="secondary"
            />
          </View>
        </View>
      </ScrollView>
    </View>
  )
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: '#DCF0F2',
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    ...defaultStyles.boxBorder,
    width: '100%',
    maxWidth: 900,
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: 25,
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  logo: {
    width: 56,
    height: 56,
  },
  heading: {
    fontSize: 30,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 10,
  },
  metaText: {
    textAlign: 'center',
    marginBottom: 20,
    color: '#3E4E63',
  },
  bodyText: {
    textAlign: 'center',
    marginBottom: 20,
  },
  htmlContainer: {
    marginBottom: 20,
  },
  htmlView: {
    width: '100%',
  },
  buttonStack: {
    gap: 10,
  },
  errorText: {
    color: '#ED4E5F',
    marginBottom: 12,
    textAlign: 'center',
  },
})

export default TermsAndConditionsGate
